import React, { memo, useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
    Label,
    Text,
    Markdown,
    Input,
    Select,
    Button,
    Link,
    CheckboxGroup,
    Icon,
    useTheme,
} from 'hoi-poi-ui-next';
import * as Tracking from 'utils/tracking';
import { REG_EX_PHONE_SIMPLE } from 'constants/Constants';
import { GeoIPService } from 'services';

const SignupExtra = memo(({ t, onStep, active }) => {
    const theme = useTheme();
    const geoData = useRef();
    const inputRef = useRef();
    const [form, setForm] = useState({});
    const [errors, setErrors] = useState();

    const sizeTeamOptions = useMemo(
        () => [
            {
                label: '1 (Freelance)',
                value: '1 (Freelance)',
            },
            {
                label: '2-4',
                value: '2-4',
            },
            {
                label: '5-10',
                value: '5-10',
            },
            {
                label: '11-50',
                value: '11-50',
            },
            {
                label: '51-99',
                value: '51-99',
            },
            {
                label: '>100',
                value: '>100',
            },
        ],
        [],
    );

    const roleOptions = useMemo(
        () => [
            {
                label: t('label_signup_role_managing_director'),
                value: 'director_general',
            },
            {
                label: t('label_signup_role_commercial_director'),
                value: 'director_comercial',
            },
            {
                label: t('label_signup_role_technology'),
                value: 'technology',
            },
            {
                label: t('label_signup_role_sales_rep'),
                value: 'comercial',
            },
            {
                label: t('label_signup_role_other'),
                value: 'other',
            },
        ],
        [t],
    );

    const checkOptions = useMemo(
        () => [
            {
                label: (
                    <Markdown
                        content={t('label_signup_privacy') + ' *'}
                        overrides={{ a: { target: '_blank' } }}
                    />
                ),
                value: 'privacyAndTermsPolicy',
            },
            {
                label: (
                    <Text type="body" color="neutral900">
                        {t('label_newsletter')}
                    </Text>
                ),
                value: 'newsletter',
            },
        ],
        [t],
    );

    const onChange = (field) => (value) => {
        if (errors) setErrors({ ...errors, [field]: null });
        setForm({ ...form, [field]: value });
    };

    const onBlurPrefix = (value) => {
        if (errors) setErrors({ ...errors, phonePrefix: null });
        let finalValue = value;
        if (!value.includes('+') && value) finalValue = '+' + value;
        else if (!value) finalValue = `+${geoData.current?.calling_code}`;
        setForm({ ...form, phonePrefix: finalValue });
    };

    const onContinue = useCallback(
        (e) => {
            e?.preventDefault?.();
            let newErrors = {};

            // Requireds
            if (!form.companyName) newErrors.companyName = t('label_field_required');
            if (!form.teamSize) newErrors.teamSize = t('label_field_required');
            if (!form.role) newErrors.role = t('label_field_required');
            if (!form.checks?.privacyAndTermsPolicy)
                newErrors.checks = { privacyAndTermsPolicy: t('label_field_required') };

            // other validations
            if (form.phone && !form.phone.trim().match(REG_EX_PHONE_SIMPLE)?.[0])
                newErrors.phone = t('label_phone_invalid');

            if (Object.keys(newErrors)?.length) {
                setErrors(newErrors);
                setForm({
                    ...form,
                    phonePrefix: form.phonePrefix || `+${geoData.current?.calling_code}`,
                });
            } else {
                setErrors(null);
                onStep(1, form);
            }
        },
        [form, onStep, t],
    );

    const onPrevious = useCallback(() => {
        onStep(-2);
    }, [onStep]);

    useEffect(() => {
        if (geoData.current) return;
        GeoIPService.getGeoData()
            .then((data) => {
                geoData.current = data;
                setForm({
                    ...form,
                    phonePrefix: '+' + data.calling_code,
                    longitude: data.longitude,
                    latitude: data.latitude,
                    timeZone: data.time_zone?.name,
                });
            })
            .catch((e) => {
                console.error(e);
                setForm({
                    ...form,
                    phonePrefix: '+34',
                    longitude: '2.1701',
                    latitude: '41.387',
                    timeZone: 'Europe/Madrid',
                });
            });
    }, [form, onStep]);

    useEffect(() => {
        if (active) {
            inputRef.current.focus();
            Tracking.logEvent({
                event: 'signup',
                functionality: 'experienceForm',
                platform: 'web',
            });
        }
    }, [active]);

    return (
        <div className="signup__extra">
            <Text type="h6" isHighlighted>
                {t('label_signup_personalize_title')}
            </Text>
            <form onSubmit={onContinue}>
                <Input
                    ref={inputRef}
                    label={t('label_signup_company_name')}
                    placeholder={t('label_signup_company_name_placeholder')}
                    onChange={onChange('companyName')}
                    onEnter={onContinue}
                    value={form.companyName}
                    type="text"
                    isRequired
                    isFullWidth
                    error={errors?.companyName}
                />

                <Label className="signup__extra__phone-label">
                    {t('label_signup_phone_number')}
                </Label>
                <div className="signup__phone">
                    <Input
                        onChange={onChange('phonePrefix')}
                        onEnter={onContinue}
                        onBlur={onBlurPrefix}
                        value={form.phonePrefix}
                        type="text"
                        isFullWidth
                        hideClear
                        error={errors?.phonePrefix}
                    />
                    <Input
                        onChange={onChange('phone')}
                        onEnter={onContinue}
                        value={form.phone}
                        type="tel"
                        isFullWidth
                        error={errors?.phone}
                    />
                </div>
                <Select
                    label={t('label_signup_size')}
                    placeholder={t('label_select_placeholder')}
                    options={sizeTeamOptions}
                    onChange={onChange('teamSize')}
                    value={form.teamSize}
                    isRequired
                    isFullWidth
                    error={errors?.teamSize}
                />
                <Select
                    label={t('label_signup_role')}
                    placeholder={t('label_select_placeholder')}
                    options={roleOptions}
                    onChange={onChange('role')}
                    value={form.role}
                    isRequired
                    isFullWidth
                    error={errors?.role}
                />
                <CheckboxGroup
                    options={checkOptions}
                    onChange={onChange('checks')}
                    value={form.checks}
                    isFullWidth
                    error={errors?.checks}
                    color="orange"
                />
            </form>
            <footer>
                <Link className="signup__link" variation="primary" onClick={onPrevious}>
                    <Icon name="chevronLeft" color={theme.colors.orange500} />
                    {t('label_previous')}
                </Link>
                <Button size="big" onClick={onContinue}>
                    {t('action_start_free_trial_login')}
                </Button>
            </footer>
        </div>
    );
});

export default SignupExtra;
